import React, { useState, useRef, useMemo } from 'react';
import Modal from '../ui/Modal';
import { formatDateYYYYMMDD, getWeekStartDate, getWeekDates, formatDayAndDate } from '../../utils/dateUtils';
import { Icons } from '../ui/Icons';
import { DayOfWeek, Schedule, WeeklyMetrics, DAY_INDEX_MAP } from '../../types';
import { toast } from 'react-hot-toast';

type SchedulablePerson = any; // Flexible typing to avoid compilation issues

interface CSVImportModalProps {
    onClose: () => void;
    people: SchedulablePerson[];
    schedule: Schedule;
    weeklyMetrics: WeeklyMetrics;
    setSchedule: React.Dispatch<React.SetStateAction<Schedule>>;
    setWeeklyMetrics: React.Dispatch<React.SetStateAction<WeeklyMetrics>>;
    weekStartDay: DayOfWeek;
    currentWeekStartDate: Date;
    setCurrentWeekStartDate: (date: Date) => void;
}

interface ParsedRow {
    rowNumber: number;
    rawDate: string;
    normalizedDate: string | null;  // YYYY-MM-DD
    arrivals: number;
    departures: number;
    stayOvers: number;
    occupancy: number; // occupancy percent
    isValid: boolean;
    error: string | null;
}

interface ImportSummary {
    metricsUpdated: number;
    newDaysCreated: number;
    existingWeeksUpdated: number;
    skippedRows: number;
}

export const CSVImportModal: React.FC<CSVImportModalProps> = ({
    onClose,
    people,
    schedule,
    weeklyMetrics,
    setSchedule,
    setWeeklyMetrics,
    weekStartDay,
    currentWeekStartDate,
    setCurrentWeekStartDate,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragActive, setDragActive] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
    const [importErrors, setImportErrors] = useState<string[]>([]);
    const [hasOverlap, setHasOverlap] = useState(false);
    const [overlapDates, setOverlapDates] = useState<string[]>([]);
    const [conflictResolution, setConflictResolution] = useState<'replace' | 'skip' | null>(null);
    const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);

    // Save initial state for standard Multi-Level Undo feature
    const [previousWeeklyMetrics, setPreviousWeeklyMetrics] = useState<WeeklyMetrics | null>(null);
    const [previousSchedule, setPreviousSchedule] = useState<Schedule | null>(null);
    const [isUndoAvailable, setIsUndoAvailable] = useState(false);

    // Date normalization function
    const normalizeDateString = (dateStr: string): string | null => {
        if (!dateStr) return null;
        const clean = dateStr.trim().replace(/^['"﻿]|['"]$/g, ''); // Strip quotes and BOM
        if (!clean) return null;

        // 1. Detect YYYY-MM-DD or YYYY/M/D
        const ymdMatch = clean.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
        if (ymdMatch) {
            const year = parseInt(ymdMatch[1], 10);
            const month = parseInt(ymdMatch[2], 10);
            const day = parseInt(ymdMatch[3], 10);
            return formatToYYYYMMDD(year, month, day);
        }

        // 2. Detect MM/DD/YYYY or M/D/YY or M/D/YYYY
        const mdyMatch = clean.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/);
        if (mdyMatch) {
            const month = parseInt(mdyMatch[1], 10);
            const day = parseInt(mdyMatch[2], 10);
            let year = parseInt(mdyMatch[3], 10);
            if (year < 100) {
                year = year < 50 ? 2000 + year : 1900 + year;
            }
            return formatToYYYYMMDD(year, month, day);
        }

        // 3. Detect verbal formatted dates matching "4-May", "May-4", "4-May-26", "4-May-2026"
        const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
        const fullMonths = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
        
        const tokens = clean.toLowerCase().split(/[-/,\s]+/);
        if (tokens.length >= 2) {
            let day: number | null = null;
            let monthIdx: number | null = null;
            let year: number = 2026; // Default/current workspace year as per instructions

            for (const token of tokens) {
                const mIdx = months.indexOf(token.slice(0, 3));
                const fmIdx = fullMonths.indexOf(token);
                if (mIdx !== -1) {
                    monthIdx = mIdx;
                } else if (fmIdx !== -1) {
                    monthIdx = fmIdx;
                } else {
                    const num = parseInt(token, 10);
                    if (!isNaN(num)) {
                        if (num > 31) {
                            year = num < 100 ? (num < 50 ? 2000 + num : 1900 + num) : num;
                        } else if (day === null) {
                            day = num;
                        }
                    }
                }
            }

            // Check if there are 3 tokens and the last token is a year
            if (tokens.length === 3) {
                const lastToken = tokens[2];
                const yearNum = parseInt(lastToken, 10);
                if (!isNaN(yearNum)) {
                    if (lastToken.length === 2 || lastToken.length === 4) {
                        year = yearNum < 100 ? (yearNum < 50 ? 2000 + yearNum : 1900 + yearNum) : yearNum;
                    }
                }
            }

            if (day !== null && monthIdx !== null) {
                return formatToYYYYMMDD(year, monthIdx + 1, day);
            }
        }

        // 4. Try JS native Date parser as fallback
        try {
            const d = new Date(clean);
            if (!isNaN(d.getTime())) {
                return formatToYYYYMMDD(d.getFullYear(), d.getMonth() + 1, d.getDate());
            }
        } catch (e) {}

        return null;
    };

    const formatToYYYYMMDD = (year: number, month: number, day: number): string => {
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${year}-${pad(month)}-${pad(day)}`;
    };

    const parseNumeric = (val: string): number => {
        if (!val) return 0;
        const clean = val.replace(/[\s,%\(\)]/g, '').trim();
        const parsed = parseInt(clean, 10);
        return isNaN(parsed) ? 0 : Math.max(0, parsed);
    };

    const parseCSVLine = (line: string): string[] => {
        const result: string[] = [];
        let curVal = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') {
                inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
                result.push(curVal.trim());
                curVal = '';
            } else {
                curVal += char;
            }
        }
        result.push(curVal.trim());
        return result;
    };

    // Main CSV parsing workflow
    const handleCSVText = (text: string) => {
        const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
        if (lines.length < 2) {
            toast.error("The CSV file is empty or lacks data rows!");
            return;
        }

        const headers = parseCSVLine(lines[0]).map(h => h.trim().replace(/^['"﻿]|['"]$/g, ''));
        
        // Find Column Indices Dynamically
        const dateIdx = headers.findIndex(h => h.toLowerCase().includes('date'));
        const stayoverIdx = headers.findIndex(h => h.toLowerCase().includes('stayover'));
        const arrivalsIdx = headers.findIndex(h => h.toLowerCase().includes('arrival') || h.toLowerCase() === 'arr');
        const dueOutIdx = headers.findIndex(h => h.toLowerCase().includes('dueout') || h.toLowerCase().includes('due out') || h.toLowerCase().includes('departure') || h.toLowerCase() === 'dep');
        const occIdx = headers.findIndex(h => h.toLowerCase().includes('occpercent') || h.toLowerCase().includes('occupancypercent') || h.toLowerCase().includes('occ%'));

        // Validate required headers
        const missingHeaders: string[] = [];
        if (dateIdx === -1) missingHeaders.push("Date (e.g., IDS_DATE)");
        if (stayoverIdx === -1) missingHeaders.push("Stayover");
        if (arrivalsIdx === -1) missingHeaders.push("Arrivals");
        if (dueOutIdx === -1) missingHeaders.push("DueOut (Departures)");
        if (occIdx === -1) missingHeaders.push("OccPercent");

        if (missingHeaders.length > 0) {
            toast.error(`Missing required CSV columns: ${missingHeaders.join(', ')}`);
            return;
        }

        const rows: ParsedRow[] = [];
        const errors: string[] = [];
        const uniqueDatesCheck = new Set<string>();

        for (let i = 1; i < lines.length; i++) {
            const rawRow = parseCSVLine(lines[i]);
            const rowNo = i + 1;

            if (rawRow.length < headers.length) {
                errors.push(`Row ${rowNo}: Missing columns. Expected ${headers.length}, found ${rawRow.length}.`);
                continue;
            }

            const rawDate = rawRow[dateIdx] || '';
            const normalized = normalizeDateString(rawDate);
            
            if (!normalized) {
                errors.push(`Row ${rowNo}: Invalid or unrecognized date format for raw value "${rawDate}".`);
                continue;
            }

            const parsedRow: ParsedRow = {
                rowNumber: rowNo,
                rawDate,
                normalizedDate: normalized,
                arrivals: parseNumeric(rawRow[arrivalsIdx]),
                departures: parseNumeric(rawRow[dueOutIdx]),
                stayOvers: parseNumeric(rawRow[stayoverIdx]),
                occupancy: parseNumeric(rawRow[occIdx]),
                isValid: true,
                error: null,
            };

            // Safeguard values
            if (isNaN(parsedRow.arrivals) || isNaN(parsedRow.departures) || isNaN(parsedRow.stayOvers) || isNaN(parsedRow.occupancy)) {
                errors.push(`Row ${rowNo}: Contains non-numeric metric values (Date: ${normalized}).`);
                parsedRow.isValid = false;
                parsedRow.error = "Non-numeric metric data";
            }

            if (parsedRow.isValid) {
                rows.push(parsedRow);
                uniqueDatesCheck.add(normalized);
            }
        }

        if (rows.length === 0) {
            toast.error("No valid operational rows were successfully parsed from the CSV.");
            setImportErrors(errors);
            return;
        }

        // Identify dates that already have metrics in React state
        const detectedOverlap: string[] = [];
        rows.forEach(r => {
            if (r.normalizedDate && weeklyMetrics[r.normalizedDate]) {
                const existing = weeklyMetrics[r.normalizedDate];
                const hasExistingData = (existing.arrivals !== 0 || existing.departures !== 0 || existing.stayOvers !== 0 || (existing.occupancy && existing.occupancy !== 0));
                if (hasExistingData && !detectedOverlap.includes(r.normalizedDate)) {
                    detectedOverlap.push(r.normalizedDate);
                }
            }
        });

        setParsedRows(rows);
        setImportErrors(errors);
        
        if (detectedOverlap.length > 0) {
            setHasOverlap(true);
            setOverlapDates(detectedOverlap);
            setConflictResolution(null); // Force user to choose
        } else {
            setHasOverlap(false);
            setOverlapDates([]);
            setConflictResolution('replace'); // No conflict, run standard overwrite
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            setFile(selectedFile);
            const reader = new FileReader();
            reader.onload = (evt) => {
                if (evt.target?.result && typeof evt.target.result === 'string') {
                    handleCSVText(evt.target.result);
                }
            };
            reader.readAsText(selectedFile);
        }
    };

    // Drag and Drop handlers
    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const selectedFile = e.dataTransfer.files[0];
            if (!selectedFile.name.endsWith('.csv')) {
                toast.error("Only CSV files are supported!");
                return;
            }
            setFile(selectedFile);
            const reader = new FileReader();
            reader.onload = (evt) => {
                if (evt.target?.result && typeof evt.target.result === 'string') {
                    handleCSVText(evt.target.result);
                }
            };
            reader.readAsText(selectedFile);
        }
    };

    // Run final operational updates & schedule containers setup
    const executeImport = () => {
        if (parsedRows.length === 0) return;
        setIsProcessing(true);

        // Pre-save initial state snapshots for UNDO capabilities
        setPreviousWeeklyMetrics(JSON.parse(JSON.stringify(weeklyMetrics)));
        setPreviousSchedule(JSON.parse(JSON.stringify(schedule)));

        const resolution = conflictResolution || 'replace';
        let updatedMetrics = { ...weeklyMetrics };
        let updatedSchedule = { ...schedule };

        let metricsUpdatedCount = 0;
        let newDaysCreatedCount = 0;
        const affectedWeeks = new Set<string>();

        // Safe Merge for Duplicate Dates in the uploaded CSV
        const mergedCSVRows: { [dateStr: string]: ParsedRow } = {};
        parsedRows.forEach(row => {
            if (!row.normalizedDate) return;
            const ds = row.normalizedDate;
            if (mergedCSVRows[ds]) {
                // Aggregate safely
                mergedCSVRows[ds].arrivals += row.arrivals;
                mergedCSVRows[ds].departures += row.departures;
                mergedCSVRows[ds].stayOvers += row.stayOvers;
                mergedCSVRows[ds].occupancy = Math.max(mergedCSVRows[ds].occupancy, row.occupancy);
            } else {
                mergedCSVRows[ds] = { ...row };
            }
        });

        const startDayIndex = DAY_INDEX_MAP[weekStartDay];

        Object.values(mergedCSVRows).forEach(row => {
            const ds = row.normalizedDate!;
            
            // Check if conflict resolution instructs us to skip existing metrics
            const exists = weeklyMetrics[ds];
            const hasExistingData = exists && (exists.arrivals !== 0 || exists.departures !== 0 || exists.stayOvers !== 0 || (exists.occupancy && exists.occupancy !== 0));
            
            if (hasExistingData && resolution === 'skip') {
                return; // Guard/Skip as requested
            }

            // Write metrics
            updatedMetrics[ds] = {
                arrivals: row.arrivals,
                departures: row.departures, // DueOut maps to departures
                stayOvers: row.stayOvers,
                occupancy: row.occupancy,
            };
            metricsUpdatedCount++;

            // Track affected week start
            const rowDate = new Date(ds + 'T00:00:00');
            const wStart = getWeekStartDate(rowDate, startDayIndex);
            const wStartStr = formatDateYYYYMMDD(wStart);
            affectedWeeks.add(wStartStr);

            // Auto-create missing day entries in schedule if they do not exist
            people.forEach(person => {
                if (!updatedSchedule[person.id]) {
                    updatedSchedule[person.id] = {};
                }
                if (!updatedSchedule[person.id][ds]) {
                    // Create unassigned placeholder Day structure (minimum structure)
                    updatedSchedule[person.id][ds] = {
                        staffId: person.id,
                        shiftId: null,
                        status: 'UNASSIGNED',
                        isForced: false
                    };
                    newDaysCreatedCount++;
                }
            });
        });

        // Set State
        setWeeklyMetrics(updatedMetrics);
        setSchedule(updatedSchedule);

        // Success summary feedback statistics
        setImportSummary({
            metricsUpdated: metricsUpdatedCount,
            newDaysCreated: newDaysCreatedCount,
            existingWeeksUpdated: affectedWeeks.size,
            skippedRows: (parsedRows.length - metricsUpdatedCount) + importErrors.length,
        });

        setIsUndoAvailable(true);
        setIsProcessing(false);
        toast.success("CSV Operational metrics successfully imported!");

        // Auto-navigate calendar UI view to the starting imported date for instant refresh
        if (parsedRows.length > 0) {
            const sortedDates = parsedRows
                .map(r => r.normalizedDate!)
                .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
            
            if (sortedDates[0]) {
                const targetFirstDate = new Date(sortedDates[0] + 'T00:00:00');
                const weekStart = getWeekStartDate(targetFirstDate, startDayIndex);
                setCurrentWeekStartDate(weekStart);
            }
        }
    };

    // Rollback last processed state modifications instantly
    const triggerUndo = () => {
        if (!previousWeeklyMetrics || !previousSchedule) return;
        setWeeklyMetrics(previousWeeklyMetrics);
        setSchedule(previousSchedule);
        setIsUndoAvailable(false);
        setImportSummary(null);
        setFile(null);
        setParsedRows([]);
        toast.success("Successfully rolled back last imported metrics!");
    };

    const previewData = useMemo(() => {
        return parsedRows.slice(0, 5);
    }, [parsedRows]);

    return (
        <Modal title="Import Operational Metrics from CSV" onClose={onClose} maxWidthClass="sm:max-w-3xl">
            <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900/40 text-gray-700 dark:text-gray-300">
                <div className="p-6 overflow-y-auto max-h-[70vh] space-y-6">
                    
                    {/* State 1: Success / Summary Dashboard */}
                    {importSummary && (
                        <div className="space-y-4 animate-fade-in">
                            <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 text-emerald-800 dark:text-emerald-400 rounded-lg flex items-start gap-3">
                                <Icons.Check className="w-6 h-6 shrink-0 text-emerald-600 dark:text-emerald-400 mt-0.5" />
                                <div>
                                    <h4 className="font-bold">Metrics CSV Process Completed!</h4>
                                    <p className="text-sm">The operational records were compiled and deployed to Firebase. The scheduler views updated in real-time.</p>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden shadow-sm">
                                <div className="p-4 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50">
                                    <h5 className="font-bold text-gray-800 dark:text-gray-100 flex items-center gap-1.5 text-sm uppercase tracking-wider">
                                        <Icons.BarChart className="w-4 h-4 text-indigo-500" />
                                        Import Action Summary
                                    </h5>
                                </div>
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-gray-100/30 dark:bg-slate-900/20 text-xs font-semibold uppercase tracking-wider text-gray-500">
                                        <tr>
                                            <th className="px-4 py-2">Action / Category</th>
                                            <th className="px-4 py-2 text-right">Count</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                                        <tr>
                                            <td className="px-4 py-2.5 font-medium">Metrics Updated</td>
                                            <td className="px-4 py-2.5 text-right font-bold text-teal-600 dark:text-teal-400">{importSummary.metricsUpdated}</td>
                                        </tr>
                                        <tr>
                                            <td className="px-4 py-2.5 font-medium">New Day entries Created</td>
                                            <td className="px-4 py-2.5 text-right font-bold text-indigo-600 dark:text-indigo-400">{importSummary.newDaysCreated}</td>
                                        </tr>
                                        <tr>
                                            <td className="px-4 py-2.5 font-medium">Weekly Schedule Structures Synced</td>
                                            <td className="px-4 py-2.5 text-right font-bold text-blue-600 dark:text-blue-400">{importSummary.existingWeeksUpdated}</td>
                                        </tr>
                                        <tr className="bg-gray-50/20 dark:bg-slate-700/10">
                                            <td className="px-4 py-2.5 font-medium text-gray-500">Skipped Rows / Errors</td>
                                            <td className="px-4 py-2.5 text-right font-bold text-gray-500">{importSummary.skippedRows}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* Skipped Errors Accordion Section */}
                            {importErrors.length > 0 && (
                                <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-lg space-y-2">
                                    <h5 className="text-xs font-bold uppercase tracking-wider text-red-600 dark:text-red-400 flex items-center gap-1">
                                        <Icons.AlertTriangle className="w-4 h-4 text-red-500" />
                                        Parsed Skipped/Error Log ({importErrors.length})
                                    </h5>
                                    <div className="max-h-28 overflow-y-auto text-xs font-mono bg-white dark:bg-slate-900 p-2.5 border border-red-500/10 rounded space-y-1">
                                        {importErrors.map((err, idx) => (
                                            <div key={idx} className="text-red-600 dark:text-red-400">● {err}</div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {isUndoAvailable && (
                                <div className="p-3.5 bg-amber-500/5 border border-amber-500/20 rounded-lg flex items-center justify-between">
                                    <div className="text-xs text-amber-800 dark:text-amber-400 pr-4">
                                        Want to discard changes? Click roll-back to undo the operations.
                                    </div>
                                    <button
                                        type="button"
                                        onClick={triggerUndo}
                                        className="h-8 px-3 font-semibold text-xs border border-amber-300 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/30 text-amber-800 dark:text-amber-300 rounded flex items-center gap-1 transition"
                                    >
                                        Undo Last Import
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* State 2: Upload Prompt & Drag drop card */}
                    {!importSummary && (
                        <div className="space-y-5">
                            
                            {/* Drag & Drop File Container */}
                            <div
                                onDragEnter={handleDrag}
                                onDragOver={handleDrag}
                                onDragLeave={handleDrag}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                                className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200 ${
                                    dragActive
                                        ? "border-teal-500 bg-teal-50/30 dark:bg-teal-900/15 scale-[0.99] shadow-inner"
                                        : "border-gray-300 dark:border-slate-700 hover:border-gray-400 dark:hover:border-slate-600 hover:bg-gray-100/30 dark:hover:bg-slate-800/20"
                                }`}
                            >
                                <input
                                    type="file"
                                    accept=".csv"
                                    ref={fileInputRef}
                                    className="hidden"
                                    onChange={handleFileChange}
                                />
                                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-150 dark:border-slate-700 text-teal-600 dark:text-teal-400 mb-4">
                                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                    </svg>
                                </div>
                                <h4 className="font-bold text-gray-800 dark:text-gray-200">
                                    {file ? file.name : "Drag and drop your report CSV file here"}
                                </h4>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-sm">
                                    or click to explore files on your device. Supports standard <code>.csv</code> reports with column mapping.
                                </p>
                            </div>

                            {/* CSV Headers Instructions */}
                            <div className="p-4 bg-slate-100 dark:bg-slate-800/40 border border-gray-200 dark:border-slate-700 rounded-xl space-y-2 text-xs">
                                <h5 className="font-bold uppercase tracking-wider text-gray-600 dark:text-gray-400 flex items-center gap-1">
                                    <Icons.Info className="w-3.5 h-3.5" /> Required Column Mapping Headers
                                </h5>
                                <p className="text-gray-500 dark:text-gray-400">
                                    We dynamically match case-insensitive column headers. Your file should contain the corresponding values:
                                </p>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 font-mono text-center pt-1 text-[11px]">
                                    <div className="p-1 px-2 border border-slate-250 dark:border-slate-750 rounded bg-white dark:bg-slate-800 text-teal-700 dark:text-teal-400">Date (e.g., IDS_DATE)</div>
                                    <div className="p-1 px-2 border border-slate-250 dark:border-slate-750 rounded bg-white dark:bg-slate-800 text-emerald-700 dark:text-emerald-400">Arrivals (e.g. Arrivals)</div>
                                    <div className="p-1 px-2 border border-slate-250 dark:border-slate-750 rounded bg-white dark:bg-slate-800 text-rose-700 dark:text-rose-450">DueOut (Departures)</div>
                                    <div className="p-1 px-2 border border-slate-250 dark:border-slate-750 rounded bg-white dark:bg-slate-800 text-blue-700 dark:text-blue-400">StayOver</div>
                                </div>
                                <p className="text-gray-450 text-[10px] pt-1 leading-relaxed">
                                    Supports dates formatted as: <code>YYYY-MM-DD</code>, <code>MM/DD/YYYY</code>, verbal <code>4-May</code> / <code>4-May-26</code>, and standard occupancy % handles strings like <code>100.00 %</code> or <code>75.14 %</code> safely.
                                </p>
                            </div>

                            {/* Live Validation & Conflict Choice Segment */}
                            {parsedRows.length > 0 && (
                                <div className="space-y-4 pt-1 border-t dark:border-slate-750 animate-fade-in">
                                    
                                    {/* Preview Segment */}
                                    <div className="space-y-2">
                                        <h5 className="text-xs font-bold uppercase tracking-wider text-gray-500">First 5 records preview:</h5>
                                        <div className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-x-auto shadow-sm">
                                            <table className="w-full text-left text-xs bg-white dark:bg-slate-800">
                                                <thead className="bg-gray-100/50 dark:bg-slate-900/40 text-[10px] font-bold text-gray-500 uppercase tracking-wide">
                                                    <tr>
                                                        <th className="px-3 py-1.5">Row</th>
                                                        <th className="px-3 py-1.5">Parsed Date</th>
                                                        <th className="px-3 py-1.5 text-center">Arrivals</th>
                                                        <th className="px-3 py-1.5 text-center">StayOver</th>
                                                        <th className="px-3 py-1.5 text-center">DueOut</th>
                                                        <th className="px-3 py-1.5 text-center">Occ %</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100 dark:divide-slate-700 font-mono">
                                                    {previewData.map(r => (
                                                        <tr key={r.rowNumber} className="hover:bg-slate-50/20">
                                                            <td className="px-3 py-1.5 text-gray-400">#{r.rowNumber}</td>
                                                            <td className="px-3 py-1.5 font-bold text-gray-700 dark:text-gray-300">{r.normalizedDate}</td>
                                                            <td className="px-3 py-1.5 text-center text-emerald-600 dark:text-emerald-400">{r.arrivals}</td>
                                                            <td className="px-3 py-1.5 text-center text-blue-600 dark:text-blue-400">{r.stayOvers}</td>
                                                            <td className="px-3 py-1.5 text-center text-rose-600 dark:text-rose-400">{r.departures}</td>
                                                            <td className="px-3 py-1.5 text-center text-amber-600 dark:text-amber-400">{r.occupancy}%</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* Overlap & Conflict handler */}
                                    {hasOverlap && (
                                        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg space-y-3 animate-pulse-subtle">
                                            <div className="flex gap-2.5">
                                                <Icons.AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
                                                <div>
                                                    <h5 className="font-bold text-amber-900 dark:text-amber-400">Overlap Detected!</h5>
                                                    <p className="text-xs text-amber-800 dark:text-amber-540 leading-relaxed">
                                                        Metrics already exist in Firebase for {overlapDates.length} of the imported dates. Select your override approach below:
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex flex-col sm:flex-row gap-2.5 pt-1.5">
                                                <button
                                                    type="button"
                                                    onClick={() => setConflictResolution('replace')}
                                                    className={`flex-1 py-1.5 px-3 text-xs font-semibold rounded shadow-sm border transition flex items-center justify-center gap-1 ${
                                                        conflictResolution === 'replace'
                                                            ? "bg-amber-600 text-white border-amber-700 outline-none"
                                                            : "bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-slate-700 hover:bg-gray-50"
                                                    }`}
                                                >
                                                    Replace Existing (Overwrite)
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setConflictResolution('skip')}
                                                    className={`flex-1 py-1.5 px-3 text-xs font-semibold rounded shadow-sm border transition flex items-center justify-center gap-1 ${
                                                        conflictResolution === 'skip'
                                                            ? "bg-amber-600 text-white border-amber-700 outline-none"
                                                            : "bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-slate-700 hover:bg-gray-50"
                                                    }`}
                                                >
                                                    Skip Existing (Keep Current)
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Errors list for dirty rows */}
                                    {importErrors.length > 0 && (
                                        <div className="p-3 bg-red-1000/10 border border-red-500/15 rounded-lg">
                                            <div className="text-xs font-bold text-red-650 dark:text-red-400">
                                                Parsed skipping {importErrors.length} invalid csv records
                                            </div>
                                        </div>
                                    )}

                                </div>
                            )}

                        </div>
                    )}

                </div>

                {/* Footer Controls */}
                <div className="bg-gray-50 dark:bg-slate-900/60 px-6 py-4 border-t border-gray-200 dark:border-slate-700 flex flex-col sm:flex-row-reverse gap-2 sm:gap-3 rounded-b-lg">
                    {importSummary ? (
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-full sm:w-auto px-5 py-2 text-sm font-semibold rounded-lg shadow bg-indigo-600 hover:bg-indigo-700 text-white transition focus:outline-none"
                        >
                            Done / Close
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                disabled={parsedRows.length === 0 || isProcessing || (hasOverlap && conflictResolution === null)}
                                onClick={executeImport}
                                className={`w-full sm:w-auto px-5 py-2 text-sm font-semibold rounded-lg shadow text-white transition flex items-center justify-center gap-1.5 ${
                                    parsedRows.length === 0 || isProcessing || (hasOverlap && conflictResolution === null)
                                        ? "bg-gray-400 dark:bg-slate-700 cursor-not-allowed opacity-60"
                                        : "bg-teal-600 hover:bg-teal-700"
                                }`}
                            >
                                {isProcessing && <Icons.Loader className="w-4 h-4 animate-spin" />}
                                Deploy & Process Metrics
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                className="w-full sm:w-auto px-5 py-2 text-sm font-semibold rounded-lg border border-gray-300 dark:border-slate-700 hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-700 dark:text-gray-300 transition"
                            >
                                Cancel Import
                            </button>
                        </>
                    )}
                </div>
            </div>
        </Modal>
    );
};
